// @ts-strict-ignore
import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import * as Chart from "chart.js";
import { filter, take } from "rxjs/operators";
import { AbstractHistoryChart } from "src/app/edge/history/abstracthistorychart";
import { calculateResolution } from "src/app/edge/history/shared";
import { AbstractHistoryChart as NewAbstractHistoryChart } from "src/app/shared/components/chart/abstracthistorychart";
import { ChartConstants } from "src/app/shared/components/chart/chart.constants";
import { ComponentJsonApiRequest } from "src/app/shared/jsonrpc/request/componentJsonApiRequest";
import { ChannelAddress, Currency, Edge, EdgeConfig, Service, Websocket } from "src/app/shared/shared";
import { ColorUtils } from "src/app/shared/utils/color/color.utils";
import { ChartAxis, HistoryUtils, TimeOfUseTariffUtils, YAxisType } from "src/app/shared/utils/utils";
import { Formatter } from "../../../../../../shared/components/shared/formatter";
import { GetScheduleRequest } from "../../../../../../shared/jsonrpc/request/getScheduleRequest";
import { GetScheduleResponse } from "../../../../../../shared/jsonrpc/response/getScheduleResponse";
import { Controller_Ess_TimeOfUseTariffUtils } from "../utils";

type ScheduleEntry = {
  timestamp: string;
  price?: number | null;
  consumptionPrice?: number | null;
  productionPrice?: number | null;
  state?: number | null;
  grid?: number | null;
  gridBuy?: number | null;
  gridSell?: number | null;
  production?: number | null;
  consumption?: number | null;
  ess?: number | null;
  soc?: number | null;
};

@Component({
  selector: "statePriceChart",
  templateUrl: "../../../../../history/abstracthistorychart.html",
  standalone: false,
})
export class ScheduleStateAndPriceChartComponent extends AbstractHistoryChart implements OnInit, OnChanges, OnDestroy {

  @Input({ required: true }) public refresh!: boolean;
  @Input({ required: true }) public override edge!: Edge;
  @Input({ required: true }) public component!: EdgeConfig.Component;

  private currencyLabel: Currency.Label = Currency.Label.CENT_PER_KWH;
  private currencyUnit: Currency.Unit;
  private modeLabelArray: string[] = [];

  constructor(
    protected override service: Service,
    protected override translate: TranslateService,
    private route: ActivatedRoute,
    private websocket: Websocket,
  ) {
    super("schedule-chart", service, translate);
  }

  public getChartHeight(): number {
    return TimeOfUseTariffUtils.getChartHeight(this.service.isSmartphoneResolution);
  }

  public ngOnInit() {
    this.service.startSpinner(this.spinnerId);

    const config = this.edge.getCurrentConfig();
    if (config) {
      const currency = config.getComponent("Core.Meta")?.properties["currency"];
      this.currencyLabel = Currency.getCurrencyLabelByCurrency(currency);
      this.currencyUnit = Currency.getChartCurrencyUnitLabel(currency);
    }
  }

  public async ngOnChanges() {
    this.edge.getConfig(this.websocket)
      .pipe(
        filter(config => !!config),
        take(1),
      )
      .subscribe(config => {
        const meta: EdgeConfig.Component = config?.getComponent("_meta");
        const currency: string = config?.getPropertyFromComponent<string>(meta, "currency");

        this.currencyLabel = Currency.getCurrencyLabelByCurrency(currency);
        this.currencyUnit = Currency.getChartCurrencyUnitLabel(currency);

        this.updateChart();
      });
  }

  public ngOnDestroy() {
    this.unsubscribeChartRefresh();
  }

  protected override updateChart() {
    this.autoSubscribeChartRefresh();
    this.service.startSpinner(this.spinnerId);
    this.loading = true;

    this.edge.sendRequest(
      this.websocket,
      new ComponentJsonApiRequest({
        componentId: this.component.id,
        payload: new GetScheduleRequest(),
      }),
    ).then(response => {
      const schedule = this.extractSchedule(response);

      const consumptionPriceArray = schedule.map(entry =>
        this.toNumberOrNull(entry.consumptionPrice ?? entry.price));

      const productionPriceArray = schedule.map(entry =>
        this.toNumberOrNull(entry.productionPrice));

      const priceArray = consumptionPriceArray;

      const stateArray = schedule.map(entry =>
        this.toNumberOrNull(entry.state));

      this.modeLabelArray = stateArray.map(state =>
        this.getModeLabel(state));

      const timestampArray = schedule.map(entry =>
        entry.timestamp);

      const gridBuyArray = schedule.map(entry =>
        this.getGridBuy(entry));

      const gridSellArray = schedule.map(entry =>
        this.getGridSell(entry));

      const socArray = schedule.map(entry =>
        this.toNumberOrNull(entry.soc));

      const productionArray = schedule.map(entry =>
        this.toNumberOrNull(entry.production));

      const consumptionArray = schedule.map(entry =>
        this.toNumberOrNull(entry.consumption));

      const essArray = schedule.map(entry =>
        this.toNumberOrNull(entry.ess));

      const scheduleChartData = Controller_Ess_TimeOfUseTariffUtils.getScheduleChartData(
        schedule.length,
        priceArray,
        stateArray,
        timestampArray,
        gridBuyArray,
        gridSellArray,
        socArray,
        this.translate,
        this.component.properties.controlMode,
        consumptionPriceArray,
        productionPriceArray,
        productionArray,
        consumptionArray,
        essArray,
      );

      this.colors = scheduleChartData.colors;
      this.labels = scheduleChartData.labels;
      this.datasets = scheduleChartData.datasets;

      this.loading = false;
      this.setLabel();
      this.stopSpinner();
    }).catch(reason => {
      console.error(reason);
      this.initializeChart();
      this.loading = false;
      this.stopSpinner();
    }).finally(async () => {
      this.unit = YAxisType.CURRENCY;
      await this.setOptions(this.options);
      this.applyControllerSpecificOptions();
    });
  }

  protected setLabel() {
    this.options = this.createDefaultChartOptions();
  }

  protected getChannelAddresses(): Promise<ChannelAddress[]> {
    return Promise.resolve([]);
  }

  private extractSchedule(response: unknown): ScheduleEntry[] {
    const result = (response as GetScheduleResponse)?.result as any;

    if (Array.isArray(result?.schedule)) {
      return result.schedule;
    }

    if (Array.isArray(result?.payload?.result?.schedule)) {
      return result.payload.result.schedule;
    }

    if (Array.isArray((response as any)?.payload?.result?.schedule)) {
      return (response as any).payload.result.schedule;
    }

    return [];
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private getGridBuy(entry: ScheduleEntry): number | null {
    const gridBuy = this.toNumberOrNull(entry.gridBuy);
    if (gridBuy !== null) {
      return Math.max(0, gridBuy);
    }

    const grid = this.toNumberOrNull(entry.grid);
    if (grid === null) {
      return null;
    }

    return Math.max(0, grid);
  }

  private getGridSell(entry: ScheduleEntry): number | null {
    const gridSell = this.toNumberOrNull(entry.gridSell);
    if (gridSell !== null) {
      return Math.max(0, gridSell);
    }

    const grid = this.toNumberOrNull(entry.grid);
    if (grid === null) {
      return null;
    }

    return Math.max(0, -grid);
  }

  private applyControllerSpecificOptions() {
    this.assignYAxisIds();

    const rightYaxisSoc: HistoryUtils.yAxes = {
      position: "right",
      unit: YAxisType.PERCENTAGE,
      yAxisId: ChartAxis.RIGHT,
      displayGrid: true,
      scale: {
        dynamicScale: true,
      },
    };

    const rightYAxisPower: HistoryUtils.yAxes = {
      position: "right",
      unit: YAxisType.POWER,
      yAxisId: ChartAxis.RIGHT_2,
      scale: {
        dynamicScale: true,
      },
    };

    const leftYAxis: HistoryUtils.yAxes = {
      position: "left",
      unit: YAxisType.CURRENCY,
      yAxisId: ChartAxis.LEFT,
      customTitle: this.currencyUnit,
      scale: {
        dynamicScale: true,
      },
    };

    this.options.scales.x["time"].unit = calculateResolution(
      this.service,
      this.service.historyPeriod.value.from,
      this.service.historyPeriod.value.to,
    ).timeFormat;

    this.options.scales.x["ticks"] = {
      source: "auto",
      autoSkip: false,
    };

    this.options.scales.x.ticks.color = getComputedStyle(document.documentElement)
      .getPropertyValue("--ion-color-chart-xAxis-ticks");

    this.options.scales.x.ticks.maxTicksLimit = 30;
    this.options.scales.x["offset"] = false;
    this.options.scales.x["stacked"] = false;

    this.options.scales.x.ticks.callback = function(value) {
      const date = new Date(value);
      return date.getMinutes() === 0 ? date.getHours() + ":00" : "";
    };

    this.options.plugins.tooltip.mode = "index";

    this.options.plugins.tooltip.callbacks.labelColor = (item: Chart.TooltipItem<any>) => {
      if (!item) {
        return;
      }

      const backgroundColor = item.dataset.backgroundColor;
      const borderColor = item.dataset.borderColor;

      if (Array.isArray(backgroundColor) && Array.isArray(borderColor)) {
        return {
          borderColor: borderColor[item.dataIndex],
          backgroundColor: backgroundColor[item.dataIndex],
        };
      }

      if (backgroundColor && borderColor) {
        return {
          borderColor: ColorUtils.changeOpacityFromRGBA(borderColor.toString(), 1),
          backgroundColor: backgroundColor,
        };
      }

      return;
    };

    this.options.plugins.tooltip.callbacks.label = (item: Chart.TooltipItem<any>) => {
      const label = item.dataset.label ?? "";
      const value = item.dataset.data[item.dataIndex];
      const yAxisId = item.dataset["yAxisID"];

      return this.formatTooltipLabel(label, value, yAxisId, item);
    };

    this.datasets = this.datasets.map(dataset => {
      if (Array.isArray(dataset.backgroundColor) || Array.isArray(dataset.borderColor)) {
        return dataset;
      }

      const opacity = dataset.type === "line" ? 0.2 : 0.5;

      if (dataset.backgroundColor && dataset.borderColor) {
        dataset.backgroundColor = ColorUtils.changeOpacityFromRGBA(
          dataset.backgroundColor.toString(),
          opacity,
        );
        dataset.borderColor = ColorUtils.changeOpacityFromRGBA(
          dataset.borderColor.toString(),
          1,
        );
      }

      return dataset;
    });

    this.options = NewAbstractHistoryChart.getYAxisOptions(
      this.options,
      rightYaxisSoc,
      this.translate,
      "line",
      this.datasets,
      true,
    );

    this.options = NewAbstractHistoryChart.getYAxisOptions(
      this.options,
      rightYAxisPower,
      this.translate,
      "line",
      this.datasets,
      true,
    );

    this.options.scales[ChartAxis.LEFT] = {
      ...this.options.scales[ChartAxis.LEFT],
      ...ChartConstants.DEFAULT_Y_SCALE_OPTIONS(
        leftYAxis,
        this.translate,
        "bar",
        this.datasets.filter(dataset => dataset["yAxisID"] === ChartAxis.LEFT),
        true,
      ),
    };

    this.options.scales[ChartAxis.LEFT]["stacked"] = false;
    this.options.scales[ChartAxis.LEFT].suggestedMin = 0;

    this.options.scales[Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_Y_AXIS_ID] = {
      type: "linear",
      position: "left",
      display: false,
      min: Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_AXIS_MIN,
      max: Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_AXIS_MAX,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    };

    this.options.scales[ChartAxis.RIGHT].grid.display = false;
    this.options.scales[ChartAxis.RIGHT_2].grid.display = false;

    delete this.options.scales[ChartAxis.RIGHT_2].suggestedMin;
    delete this.options.scales[ChartAxis.RIGHT_2].suggestedMax;
    delete this.options.scales[ChartAxis.RIGHT_2].min;
    delete this.options.scales[ChartAxis.RIGHT_2].max;

    this.options["animation"] = false;
  }

  private assignYAxisIds() {
    this.datasets = this.datasets.map((dataset: Chart.ChartDataset) => {
      if (dataset["yAxisID"] === Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_Y_AXIS_ID) {
        return dataset;
      }

      const label = dataset.label;

      if (label === this.translate.instant("GENERAL.SOC")) {
        dataset["yAxisID"] = ChartAxis.RIGHT;
        return dataset;
      }

      if (label === this.translate.instant("GENERAL.GRID_BUY_ADVANCED")
        || label === this.translate.instant("GENERAL.GRID_SELL")
        || label === "Grid sell"
        || label === this.translate.instant("GENERAL.PRODUCTION")
        || label === "Production"
        || label === this.translate.instant("GENERAL.CONSUMPTION")
        || label === "Consumption"
        || label === this.translate.instant("GENERAL.ESS")
        || label === "ESS") {
        dataset["yAxisID"] = ChartAxis.RIGHT_2;
        return dataset;
      }

      dataset["yAxisID"] = ChartAxis.LEFT;
      return dataset;
    });
  }

  private formatTooltipLabel(label: string, value: unknown, yAxisId: string, item: Chart.TooltipItem<any>): string {
    if (this.isModeTimelineDataset(label, yAxisId)) {
      return this.modeLabelArray[item.dataIndex] ?? "";
    }

    const numberValue = this.toNumberOrNull(value);

    if (numberValue === null) {
      return `${label}: -`;
    }

    switch (yAxisId) {
      case ChartAxis.RIGHT:
        return `${label}: ${this.formatTooltipNumber(numberValue, 0)} %`;

      case ChartAxis.RIGHT_2:
        return `${label}: ${this.formatTooltipNumber(numberValue, 2)} kW`;

      case ChartAxis.LEFT:
      default:
        return label + ": " + Formatter.FORMAT_CURRENCY_PER_KWH(numberValue, this.currencyLabel);
    }
  }

  private isModeTimelineDataset(label: string, yAxisId: string): boolean {
    return yAxisId === Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_Y_AXIS_ID
      || label === Controller_Ess_TimeOfUseTariffUtils.STATE_TIMELINE_DATASET_LABEL;
  }

  private getModeLabel(state: number | null): string {
    switch (state) {
      case 1:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.BALANCING");

      case 3:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.CHARGE_GRID");

      case 0:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.DELAY_DISCHARGE");

      case 5:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.PEAK_SHAVING");

      case 4:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.DISCHARGE_GRID");

      case 6:
        return this.translate.instant("EDGE.INDEX.WIDGETS.TIME_OF_USE_TARIFF.STATE.HOLD");

      default:
        return "";
    }
  }

  private formatTooltipNumber(value: number, fractionDigits: number): string {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
    });
  }
}
